import { useEffect, useMemo, useState } from "react";

const GOOGLE_CALLBACK_PATH = "/auth/google/callback";
const completedAttempts = new Set();
const inflightAttemptPromises = new Map();

function GoogleAuthCallback({ onRefreshAuthState, onNavigate, authOrigin }) {
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Finishing your Google sign-in...");

  const params = useMemo(() => {
    const search = new URLSearchParams(window.location.search);
    return {
      code: search.get("code") || "",
      state: search.get("state") || "",
      googleError: search.get("error") || "",
    };
  }, []);

  const attemptKey = `${params.state}:${params.code}`;

  const completeSignIn = async () => {
    if (!params.code || !params.state) {
      setStatus("error");
      setMessage("This Google sign-in link is missing information. Please try again.");
      return;
    }

    setStatus("loading");
    setMessage("Finishing your Google sign-in...");

    try {
      const apiBase = (authOrigin || "").replace(/\/$/, "");
      const completeUrl = apiBase ? `${apiBase}/api/auth/google/complete` : "/api/auth/google/complete";
      let completionPromise = inflightAttemptPromises.get(attemptKey);
      if (!completionPromise) {
        completionPromise = (async () => {
          const response = await fetch(completeUrl, {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ code: params.code, state: params.state }),
          });
          const data = await response.json().catch(() => null);

          if (!response.ok || !data?.ok) {
            throw new Error(data?.message || "Unable to complete Google sign-in");
          }

          return data;
        })();
        inflightAttemptPromises.set(attemptKey, completionPromise);
      }

      const data = await completionPromise;
      completedAttempts.add(attemptKey);
      inflightAttemptPromises.delete(attemptKey);

      setStatus("success");
      setMessage("Google sign-in complete. Redirecting...");
      window.history.replaceState({}, document.title, GOOGLE_CALLBACK_PATH);

      await onRefreshAuthState?.(data);
      onNavigate?.(data?.nextPath || "/");
    } catch (error) {
      inflightAttemptPromises.delete(attemptKey);
      console.error("google oauth completion failed", error);
      setStatus("error");
      setMessage("We couldn't finish signing you in. Please try again.");
      window.history.replaceState({}, document.title, GOOGLE_CALLBACK_PATH);
    }
  };

  useEffect(() => {
    if (params.googleError) {
      setStatus("error");
      setMessage(
        params.googleError === "access_denied"
          ? "Google sign-in was canceled. You can try again whenever you're ready."
          : "Google sign-in did not complete. Please try again."
      );
      window.history.replaceState({}, document.title, GOOGLE_CALLBACK_PATH);
      return;
    }

    if (!params.code || !params.state) {
      setStatus("error");
      setMessage("This Google sign-in link is invalid or expired. Please try again.");
      return;
    }

    if (completedAttempts.has(attemptKey)) {
      setStatus("success");
      setMessage("Google sign-in already completed. Redirecting...");
      onRefreshAuthState?.();
      onNavigate?.("/");
      return;
    }

    completeSignIn();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptKey, params.code, params.googleError, params.state]);

  return (
    <div className="oauth-callback-page">
      <div className={`oauth-callback-card state-${status}`}>
        <h1>Google Sign-In</h1>
        <p>{message}</p>
        {status === "loading" ? <div className="oauth-spinner" aria-hidden="true" /> : null}
        {status === "error" ? (
          <div className="oauth-actions">
            {params.code && params.state ? (
              <button type="button" className="oauth-action-button" onClick={completeSignIn}>
                Try again
              </button>
            ) : null}
            <button type="button" className="oauth-action-button secondary" onClick={() => onNavigate?.("/")}>
              Back to prep.ai
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default GoogleAuthCallback;
